import { ArrowLeft, Package, Copy } from 'lucide-react'
import toast, { Toaster } from 'react-hot-toast'
import { useEffect, useState } from 'react'
import { Button, Card, Col, Container, Row, Table } from 'react-bootstrap'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import api from '../../../config/AxiosInstance'
import {
  convertDateWithoutTime,
  separateNumber
} from '../../../helpers/constants'
import * as Common from '../../../utils/Common'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const cardStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#959FA9',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: '0.25rem'
}

const valueStyle = {
  fontSize: '1rem',
  fontWeight: '600',
  color: '#333'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const MasterStockDetail = () => {
  const navigate = useNavigate()

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard!', {
      position: 'top-right',
      style: {
        fontSize: '14px',
        fontWeight: '600'
      }
    })
  }
  const { code } = useParams()
  const { state } = useLocation()
  const [data, setData] = useState({})
  const [stocks, setStocks] = useState([])
  const [loading, setLoading] = useState(false)
  const [meta, setMeta] = useState({
    current_page: 1,
    per_page: 10,
    total_row: 0,
    total_page: 0
  })

  const loadData = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/master/stocks/${code}`, {
        params: {
          per_page: meta.per_page,
          page: meta.current_page,
          warehouse_id: state?.warehouse_id || '',
          type: state?.typeWarehouse
        }
      })
      setData(response.data.data)
      setStocks(response.data.data.stocks)
      setMeta(response.data.metadata)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setLoading(false)
    }
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: Number(e.target.value),
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({ ...prevMeta, current_page: data.selected + 1 }))
    window.scrollTo(0, 0)
  }

  const calculateTotalQty = (property) => {
    return (
      stocks?.reduce((total, stock) => {
        if (!stock[property]) return total
        const val = stock[property].toString().split(' ')[0]
        return total + Number(val)
      }, 0) || 0
    )
  }

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.current_page, meta.per_page])

  return (
    <Container fluid className="px-4">
      {/* Header */}
      <Row className="mb-4 align-items-center">
        <Col className="d-flex align-items-center gap-3">
          <Button
            variant="light"
            className="rounded shadow-sm border p-0 d-flex align-items-center justify-content-center"
            style={{ width: '40px', height: '40px' }}
            onClick={() => navigate('/master/stock', { state: state })}
          >
            <ArrowLeft className="text-secondary" size={20} />
          </Button>
          <div>
            <h3 style={headerTitleStyle} className="mb-0">
              Stock Detail
            </h3>
            <small className="text-muted fw-semibold">
              {data?.warehouse_name || '-'}
            </small>
          </div>
        </Col>
      </Row>

      {/* General Info Card */}
      <Row className="mb-4">
        <Col>
          <Card style={cardStyle}>
            <Row>
              <Col md={12} className="mb-3 d-flex align-items-center gap-3">
                <div
                  className="rounded d-flex align-items-center justify-content-center"
                  style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: 'rgba(248, 157, 29, 0.1)'
                  }}
                >
                  <Package className="text-primary-orange" size={24} />
                </div>
                <div>
                  <div style={labelStyle}>Item Name</div>
                  <div style={{ ...valueStyle, fontSize: '1.25rem' }}>
                    {data?.item_name || '-'}
                  </div>
                </div>
              </Col>
              <Col md={12}>
                <div className="p-3 rounded bg-light border border-light">
                  <div style={labelStyle} className="mb-2">
                    Description
                  </div>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: data?.description || '-'
                    }}
                    className="text-dark"
                    style={{ fontSize: '0.9rem', lineHeight: '1.6' }}
                  />
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card style={cardStyle}>
            <div className="mb-3 px-1">
              <h5 className="fw-bold mb-0 text-dark">Batch List</h5>
            </div>
            <Table
              hover
              responsive
              className="mb-0"
              style={{
                fontSize: '0.9rem'
              }}
            >
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Batch
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Received
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Exp Date
                  </th>
                  <th
                    className="p-2 align-middle text-end"
                    style={tableHeaderStyle}
                  >
                    Qty Kemasan
                  </th>
                  <th
                    className="p-2 align-middle text-end"
                    style={tableHeaderStyle}
                  >
                    Base Qty UOM
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Location
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Pallet ID
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7}>
                      <Loader />
                    </td>
                  </tr>
                )}
                {!loading && stocks.length === 0 && <RowEmpty colSpan={7} />}
                {!loading &&
                  stocks.length > 0 &&
                  stocks.map((stock, index) => (
                    <tr
                      key={index}
                      style={{ borderBottom: '1px solid #f0f0f0' }}
                    >
                      <td className="p-2 align-middle fw-semibold">
                        <div className="d-flex align-items-center gap-2">
                          {stock.batch}
                          {stock.batch && (
                            <Copy
                              size={14}
                              className="text-muted cursor-pointer"
                              style={{
                                cursor: 'pointer',
                                opacity: 0.5,
                                transition: 'opacity 0.2s'
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.opacity = 1)
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.opacity = 0.5)
                              }
                              onClick={() => handleCopy(stock.batch)}
                              title="Copy Batch"
                            />
                          )}
                        </div>
                      </td>
                      <td className="p-2 align-middle">
                        {stock.received
                          ? convertDateWithoutTime(stock.received)
                          : '-'}
                      </td>
                      <td className="p-2 align-middle">
                        {stock.exp_date
                          ? convertDateWithoutTime(stock.exp_date)
                          : '-'}
                      </td>
                      <td className="p-2 align-middle text-end">
                        <span className="fw-bold">{stock.total_pack}</span>{' '}
                        <span className="text-secondary small">
                          {stock.uom_pack}
                        </span>
                      </td>
                      <td className="p-2 align-middle text-end">
                        <span className="fw-bold">{stock.total_qty}</span>{' '}
                        <span className="text-secondary small">
                          {stock.uom_qty}
                        </span>
                      </td>
                      <td className="p-2 align-middle">
                        <div className="d-flex align-items-center gap-2">
                          {stock.location ? (
                            <span className="fw-bold">{stock.location}</span>
                          ) : (
                            '-'
                          )}
                          {stock.location && (
                            <Copy
                              size={14}
                              className="text-muted cursor-pointer"
                              style={{
                                cursor: 'pointer',
                                opacity: 0.5,
                                transition: 'opacity 0.2s'
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.opacity = 1)
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.opacity = 0.5)
                              }
                              onClick={() => handleCopy(stock.location)}
                              title="Copy Location"
                            />
                          )}
                        </div>
                      </td>
                      <td className="p-2 align-middle">
                        <div className="d-flex align-items-center gap-2">
                          {stock.pallet_id ? (
                            <span className="fw-bold">{stock.pallet_id}</span>
                          ) : (
                            '-'
                          )}
                          {stock.pallet_id && (
                            <Copy
                              size={14}
                              className="text-muted cursor-pointer"
                              style={{
                                cursor: 'pointer',
                                opacity: 0.5,
                                transition: 'opacity 0.2s'
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.opacity = 1)
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.opacity = 0.5)
                              }
                              onClick={() => handleCopy(stock.pallet_id)}
                              title="Copy Pallet ID"
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
              {!loading && stocks.length > 0 && (
                <tfoot style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <td colSpan={3} className="p-2 fw-bold text-end">
                      Total
                    </td>
                    <td className="p-2 fw-bold text-end text-primary-orange">
                      {separateNumber(calculateTotalQty('total_pack'))}
                    </td>
                    <td className="p-2 fw-bold text-end text-primary-orange">
                      {separateNumber(calculateTotalQty('total_qty'))}
                    </td>
                    <td className="p-2"></td>
                    <td className="p-2"></td>
                  </tr>
                  <tr>
                    <td colSpan={7} className="p-3 border-top-0">
                      <TablePagination
                        meta={meta}
                        handlePageChange={handlePageChange}
                        handleOnChangeMeta={handleOnChangeMeta}
                      />
                    </td>
                  </tr>
                </tfoot>
              )}
            </Table>
          </Card>
        </Col>
      </Row>
      <Toaster />
    </Container>
  )
}

export default MasterStockDetail
